
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button"

import { FiPlus, FiSave, FiEdit } from "react-icons/fi"
import { FaTrash } from "react-icons/fa";
import CharacterEditorModal from "@/components/modals/CharacterEditorModal"
import ConfirmModal from "@/components/modals/ConfirmModal"
import { useModal } from "@/contexts/ModalContext"
import { useShow } from "@/contexts/ShowContext"
import { apiFetch } from '@/lib/api'
import { getFileUrl } from "@/lib/s3"
import { LuLoaderCircle } from "react-icons/lu";
import useFileUpload from "@/hooks/useFileUpload"
import Loader from "@/components/loader"

const Character = ({character, onEdit, onDelete, onOpen}) =>{
    const [loading, setLoading] = useState(false)


    const handleOnEdit = async () =>{
        setLoading(true)
        if (onEdit) await onEdit(character)
        setLoading(false)
    }

    const handleOnDelete = async () =>{
        setLoading(true)
        if (onDelete) await onDelete(character)
        setLoading(false)
    }

    const generateGreeting = (character) =>{
        const greetings = [
            `Hey!`,
            `Hello!`,
            `Hi!`,
        ]

        const randomGreeting = greetings[Math.floor(Math.random() * greetings.length)]
        return `${randomGreeting} I'm ${character.name}`
    }

    return (
        <div
            className='flex flex-col gap-2 relative group cursor-pointer'
            role="button"
            tabIndex={0}
            aria-label={`Open ${character.name}`}
            onClick={() => onOpen?.(character)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen?.(character)
                }
            }}
        >
            <div className='flex justify-center items-center relative aspect-video group/thumbnail bg-secondary rounded-md overflow-hidden'>
                {character.thumbnail_url && <img src={character.thumbnail_url} alt={character.name} loading="lazy" decoding="async" style={{position:'absolute',width:'100%',height:'100%',objectFit:'cover'}} className='object-cover' />}
                <div className='absolute bottom-0 right-0 hidden group-hover:flex items-center gap-1 p-1' onClick={(e) => e.stopPropagation()}>
                    <Button type="secondary" icon={<FiEdit className="w-3 h-3" />} onClick={handleOnEdit}>Edit</Button>
                    <Button type="danger" icon={<FaTrash className="w-3 h-3" />} onClick={handleOnDelete} />
                </div>
                
                {loading && <div className='absolute top-0 left-0 w-full h-full bg-black/60 text-white flex items-center justify-center'>
                    <LuLoaderCircle className='w-6 h-6 animate-spin' />
                </div>}
            </div>
            <div className='flex gap-2 flex-col items-start px-1'>
                <span className='text-sm text-secondary-foreground'>{character.name}</span>
                <span className='text-xs text-secondary-foreground/80'>{character.description.split(' ').length > 40 ? character.description.split(' ').slice(0, 40).join(' ') + '...' : character.description}</span>
            </div>
            
        </div>
    )
}

export default function ShowCharactersPage() {
    const [updating, setUpdating] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [characters, setCharacters] = useState([])
    const [loadingStatus, setLoadingStatus] = useState('')
    const {show} = useShow()
    const {openModal} = useModal()
    const {uploadFile, uploadProgress} = useFileUpload()
    const navigate = useNavigate()

    const handleOpenCharacter = (character) => {
        navigate(`/show/characters/${character._id}/assets`)
    }

    const fetchCharacters = async () => {
        try{
            const response = await apiFetch(`/api/shows/${show._id}/characters`)
            const data = await response.json()

            if(!response.ok) throw new Error('Failed to fetch characters')
            setCharacters(data)
        }catch(error){
            setError(error.message)
        }
    }

    const handleEditCharacter = async (character) => {
        const characterData = await openModal(<CharacterEditorModal show={show} character={character}/>, 'side')
        if(!characterData)return

        try{
            let thumbnailData = {}

            if(characterData.thumbnail_file){
                thumbnailData = await uploadFile(characterData.thumbnail_file)
                delete characterData.thumbnail_file
            }

            const charResponse = await apiFetch(`/api/shows/${show._id}/characters/${characterData._id || ''}`, {
                method: characterData._id ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...characterData,
                    ...thumbnailData
                })
            })

            if(!charResponse.ok) throw new Error('Failed to update character')

            await fetchCharacters()
        }catch(error){
            console.error(error)
        }finally{
            setUpdating(false)
            setLoadingStatus('')
        }
    }

    const handleDeleteCharacter = async (character) => {
        const confirmed = await openModal(<ConfirmModal title="Delete Character" message="Are you sure you want to delete this character?" />)

        if(confirmed){
            try{
                setUpdating(true)
                const deleteResponse = await apiFetch(`/api/shows/${show._id}/characters/${character._id}`, {
                    method: 'DELETE'
                })

                if(!deleteResponse.ok) throw new Error('Failed to delete character')

                await fetchCharacters()
            }catch(error){
                console.error(error)
            }finally{
                setUpdating(false)
                setLoadingStatus('')
            }
        }
    }

    useEffect(()=> {
        const getData = async() =>{
            setLoading(true)
            await fetchCharacters()
            setLoading()
        }

        getData()
    }, [show])

    if (loading) {
        return (<Loader />)
    }

    if (error) {
        return (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <span className="text-red-400">Error: {error}</span>
        </div>
        )
    }

    return (
        <div className="flex flex-1 flex-col relative">
                <div className='flex justify-between items-center px-10 h-20'>
                    <div className='flex gap-2'>
                        <h1 className='text-2xl font-bold border-l-4 border-primary pl-4'>Characters</h1>
                    </div>
                    <div className='flex gap-2'>
                        <Button type='secondary' icon={<FiPlus className="w-4 h-4" />} onClick={handleEditCharacter}>Add Character</Button>
                    </div>
                </div>
                <div className='flex flex-col w-full overflow-hidden'>
                    <div className='w-full h-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 py-4 px-10 overflow-y-auto'>
                        {characters.map((character) => <Character key={character._id} character={character} onEdit={handleEditCharacter} onDelete={handleDeleteCharacter} onOpen={handleOpenCharacter} />)}
                    </div>
                </div>

                {updating && <Loader status={loadingStatus} />}
        </div>
  );
}
